import { BaseController, type Rx, type SubtreeModel } from "subtree.js/core";
import type { AppDeps } from "../../../../app-deps";
import {
  createTicketKind,
  deleteTicketKind,
  loadTicketKinds,
  updateTicketKind,
} from "../../../../core/ops/hosted-event-ops";
import { errorText } from "../../../../core/primitives/errors";
import { hasErrors } from "../../../../core/primitives/form-errors";
import {
  busyForm,
  failedForm,
  failedLoad,
  idleForm,
  idleLoad,
  loaded,
  loading,
} from "../../../../core/primitives/load-status";
import {
  draftToNewTicketKind,
  draftToTicketKindUpdate,
  EMPTY_TICKET_KIND,
  TICKETS_FAILED_ON_THE_WAY,
  TICKETS_FAILED_TO_LOAD,
  type TicketKindField,
  ticketKindDraft,
  validateTicketKindDraft,
} from "../../../../core/primitives/ticket-kind";
import { TicketKindsSectionActions, TicketKindsSectionState } from "./ticket-kinds-section-model";

export type TicketKindsSectionDeps = Pick<AppDeps, "client" | "authHandler" | "storeRepository">;

export interface TicketKindsSectionHost {
  readonly eventId: string;
  /** When the host cancelled the show, as the page has it. */
  readonly cancelledAt?: Rx<Date | null>;
}

/**
 * The tickets a saved show has for its visitors: kinds the host names, prices and counts.
 *
 * The section asks one thing at a time (`acting`): while a call is on its way, or the tickets are read
 * again after one that failed, every other action waits. So no answer arrives out of turn, and what is on
 * screen is what the last answer said.
 */
export class TicketKindsSectionController extends BaseController implements TicketKindsSectionActions {
  readonly state = new TicketKindsSectionState();
  #storeId: string | null = null;
  /** Counts the shops shown here, one after another: an answer asked for the last one writes nothing. */
  #visit = 0;
  /** The fields the host changed in the edit form since it opened: a save sends those and no others. */
  #typed = new Set<TicketKindField>();
  /** The kind the sentence about a removal is for. */
  #removeTried: string | null = null;

  constructor(
    subtree: SubtreeModel,
    readonly deps: TicketKindsSectionDeps,
    readonly host: TicketKindsSectionHost,
  ) {
    super();
    subtree.put(TicketKindsSectionState, this.state);
    subtree.put(TicketKindsSectionActions, this);
    this.sync(() => {
      const storeId = deps.storeRepository.shopId;
      if (storeId === this.#storeId) return;
      this.#storeId = storeId;
      this.#visit++;
      this.#forget();
      if (storeId) void this.#read(storeId);
    }, [deps.storeRepository]);
    const cancelledAt = host.cancelledAt;
    if (cancelledAt) {
      this.sync(() => {
        const cancelled = cancelledAt.value !== null;
        this.state.cancelled.value = cancelled;
        // A cancelled show's tickets stay as they are, so a form open on one closes.
        if (cancelled) this.#edit(null);
      }, [cancelledAt]);
    }
  }

  setField = (field: TicketKindField, value: string) => {
    this.state.draft.update((d) => ({ ...d, [field]: value }));
    if (this.state.errors.value[field]) this.state.errors.update(({ [field]: _, ...rest }) => rest);
  };

  add = async () => {
    const storeId = this.#storeId;
    if (!storeId || this.state.acting.value) return;
    const draft = this.state.draft.value;
    const errors = validateTicketKindDraft(draft);
    this.state.errors.value = errors;
    if (hasErrors(errors)) return;
    this.state.addForm.value = busyForm;
    const r = await this.#ask(
      storeId,
      () => createTicketKind({ ...this.deps, storeId, eventId: this.host.eventId, body: draftToNewTicketKind(draft) }),
      () => {
        this.state.addForm.value = failedForm(TICKETS_FAILED_ON_THE_WAY);
      },
    );
    if (!r) return;
    if (!r.ok) {
      this.state.addForm.value = failedForm(r.errors);
      return;
    }
    this.state.kinds.value = [...this.state.kinds.value, r.value];
    this.state.draft.value = EMPTY_TICKET_KIND;
    this.state.addForm.value = idleForm;
  };

  startEdit = (kindId: string) => {
    const kind = this.state.kinds.value.find((k) => k.id === kindId);
    if (!kind || this.state.acting.value || this.state.cancelled.value) return;
    this.#edit(kindId);
    this.state.editDraft.value = ticketKindDraft(kind);
    this.state.editErrors.value = {};
  };

  setEditField = (field: TicketKindField, value: string) => {
    this.#typed.add(field);
    this.state.editDraft.update((d) => ({ ...d, [field]: value }));
    if (this.state.editErrors.value[field]) this.state.editErrors.update(({ [field]: _, ...rest }) => rest);
  };

  saveEdit = async () => {
    const storeId = this.#storeId;
    const kindId = this.state.editingId.value;
    if (!storeId || !kindId || this.state.acting.value) return;
    const draft = this.state.editDraft.value;
    const errors = validateTicketKindDraft(draft);
    this.state.editErrors.value = errors;
    if (hasErrors(errors)) return;
    if (this.#typed.size === 0) {
      this.#edit(null);
      return;
    }
    this.state.editForm.value = busyForm;
    const body = draftToTicketKindUpdate(draft, this.#typed);
    const r = await this.#ask(
      storeId,
      () => updateTicketKind({ ...this.deps, storeId, eventId: this.host.eventId, kindId, body }),
      () => {
        this.state.editForm.value = failedForm(TICKETS_FAILED_ON_THE_WAY);
      },
    );
    if (!r) return;
    if (!r.ok) {
      this.state.editForm.value = failedForm(r.errors);
      return;
    }
    const saved = r.value;
    this.state.kinds.value = this.state.kinds.value.map((k) => (k.id === saved.id ? saved : k));
    this.#edit(null);
  };

  cancelEdit = () => {
    if (this.state.acting.value) return;
    this.#edit(null);
  };

  remove = async (kindId: string) => {
    const storeId = this.#storeId;
    if (!storeId || this.state.acting.value) return;
    this.state.removingId.value = kindId;
    this.#removeFailed(null, null);
    const r = await this.#ask(
      storeId,
      () => deleteTicketKind({ ...this.deps, storeId, eventId: this.host.eventId, kindId }),
      () => {
        this.state.removingId.value = null;
        this.#removeFailed(kindId, TICKETS_FAILED_ON_THE_WAY);
      },
    );
    if (!r) return;
    this.state.removingId.value = null;
    if (!r.ok) {
      this.#removeFailed(kindId, errorText(r.errors));
      return;
    }
    this.state.kinds.value = this.state.kinds.value.filter((k) => k.id !== kindId);
    if (this.state.editingId.value === kindId) this.#edit(null);
  };

  /**
   * One call at a time. A call that fails on its way may have gone through all the same: `failed` says so,
   * the tickets are read again before anything else is asked, and the error goes on to Sentry. The answer is
   * null when the section has moved on to another shop meanwhile.
   */
  async #ask<T>(storeId: string, call: () => Promise<T>, failed: () => void): Promise<T | null> {
    const visit = this.#visit;
    this.state.acting.value = true;
    let r: T;
    try {
      r = await call();
    } catch (err) {
      if (!this.#gone(visit)) {
        failed();
        void this.#read(storeId);
      }
      throw err;
    }
    if (this.#gone(visit)) return null;
    this.state.acting.value = false;
    return r;
  }

  #gone(visit: number): boolean {
    return this.isDisposed || visit !== this.#visit;
  }

  /** Opens the edit form on a kind, or closes it. */
  #edit(kindId: string | null): void {
    this.#typed = new Set();
    this.state.editingId.value = kindId;
    this.state.editForm.value = idleForm;
  }

  /** Another shop is on screen: nothing read, typed or asked for the last one stays. */
  #forget(): void {
    const s = this.state;
    s.status.value = idleLoad;
    s.kinds.value = [];
    s.acting.value = false;
    s.draft.value = EMPTY_TICKET_KIND;
    s.errors.value = {};
    s.addForm.value = idleForm;
    this.#edit(null);
    s.editErrors.value = {};
    s.removingId.value = null;
    this.#removeFailed(null, null);
  }

  #removeFailed(kindId: string | null, sentence: string | null): void {
    this.#removeTried = kindId;
    this.state.removeError.value = sentence;
  }

  async #read(storeId: string): Promise<void> {
    const visit = this.#visit;
    this.state.acting.value = true;
    // Read again, the tickets stay on screen with their forms and what those say.
    if (this.state.status.value.kind !== "loaded") this.state.status.value = loading;
    let r: Awaited<ReturnType<typeof loadTicketKinds>>;
    try {
      r = await loadTicketKinds({ ...this.deps, storeId, eventId: this.host.eventId });
    } catch (err) {
      if (!this.#gone(visit)) {
        this.state.acting.value = false;
        this.state.status.value = { kind: "error", message: TICKETS_FAILED_TO_LOAD };
      }
      throw err;
    }
    if (this.#gone(visit)) return;
    this.state.acting.value = false;
    if (!r.ok) {
      this.state.status.value = failedLoad(r.errors);
      return;
    }
    this.state.kinds.value = r.value.kinds;
    // A kind that is gone takes with it the form open on it and the sentence about removing it.
    const listed = (kindId: string) => r.value.kinds.some((k) => k.id === kindId);
    const editing = this.state.editingId.value;
    if (editing && !listed(editing)) this.#edit(null);
    if (this.#removeTried && !listed(this.#removeTried)) this.#removeFailed(null, null);
    this.state.status.value = loaded;
  }
}
